import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { ApplicationConfig, ErrorHandler, DEFAULT_CURRENCY_CODE, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core'
import { provideRouter, Router, withPreloading, PreloadAllModules } from '@angular/router'
import { ServiceWorkerModule } from '@angular/service-worker'

import { MessageService, ConfirmationService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'

import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

import * as Sentry from '@sentry/angular'
import { environment } from 'environments/environment'

import { LongRunningJobService } from '@app/services/long-running-job.service'
import { NetworkService } from '@app/services/network.service'
import { SignalService } from '@app/services/signal.service'
import { TitleService } from '@app/services/title.service'
import { UpdateService } from '@app/services/update.service'
import { UserService } from '@app/services/user.service'
import { UtilitiesService } from '@app/services/utilities.service'
import { ConfigureFiltersService } from '@app/components/configure-filters/configure-filters.service'
import { TourService } from '@app/components/tour/tour.service'
import { provideMsal } from '@app/auth/msal.providers'
import { GlobalErrorInterceptor } from '@app/interceptors/global-error.interceptor'
import { ImsCommitHashInterceptor } from '@app/interceptors/ims-commit-hash.interceptor'
import { LocaleInterceptor } from '@app/interceptors/locale.interceptor'

import { APP_ROUTES } from './app.routes'

const actedPresetTheme = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        primary: {
          50: '#f4f3f7',
          100: '#c8c7da',
          200: '#9d9abc',
          300: '#7A7A7A',
          400: '#464181',
          500: '#1b1464',
          600: '#171155',
          700: '#130e46',
          800: '#0f0b37',
          900: '#0b0828',
          950: '#070519',
        },
      },
      dark: {
        primary: {
          50: '#f6fbf8',
          100: '#d4eddd',
          200: '#b2dec2',
          300: '#8fcfa8',
          400: '#6dc18d',
          500: '#4bb272',
          600: '#409761',
          700: '#357d50',
          800: '#29623f',
          900: '#1e472e',
          950: '#132d1d',
        },
      },
    },
  },
  components: {
    autocomplete: {
      root: {
        disabledBackground: 'transparent',
      },
      css: () => `
        .p-autocomplete.p-disabled .p-autocomplete-dropdown {
          visibility: hidden;
        }
        .p-disabled.p-autocomplete-input-multiple {
          border: 1px solid transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
        }
        .p-disabled .p-autocomplete-token-icon,
        .p-autocomplete-multiple-container.p-disabled .p-autocomplete-token-icon {
          display: none;
        }
        .p-autocomplete-dropdown:disabled {
          visibility: hidden;
        }
      `,
    },

    button: {
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: 'var(--color-button-secondary)',
              color: 'white',
              hoverBackground: 'var(--color-button-secondary-hover)',
              hoverColor: 'white',
            },
          },
        },
        dark: {
          root: {
            secondary: {
              background: 'var(--color-button-secondary)',
              color: 'white',
              hoverBackground: 'var(--color-button-secondary-hover)',
              hoverColor: 'white',
            },
          },
        },
      },
      css: () => `
        .p-badge {
          background: orange;
        }
        .p-button.p-button-black,
        .p-buttonset.p-button-black > .p-button,
        .p-splitbutton.p-button-black > .p-button,
        .p-button.p-button-black:enabled:hover,
        .p-buttonset.p-button-black > .p-button:enabled:hover,
        .p-splitbutton.p-button-black > .p-button:enabled:hover {
          color: #fff;
          background: #000000;
          border: 1px solid #000000;
        }
      `,
    },

    checkbox: {
      root: {
        borderColor: '#ced4da',
      },
      css: () => `
        .p-checkbox.p-disabled .p-checkbox-box {
          background: transparent;
          border-color: var(--p-checkbox-border-color);
          cursor: text;
          pointer-events: auto;
        }
      `,
    },

    card: {
      root: {
        borderRadius: '16px',
      },
      colorScheme: {
        light: {
          root: {
            shadow: '0 -1px 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.07), 0 4px 8px rgba(0,0,0,0.07), 0 8px 16px rgba(0,0,0,0.07)',
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            shadow: '0 1px 2px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.15)',
            background: '{surface.900}',
          },
        },
      },
      css: () => `
        .p-card {
          backdrop-filter: blur(10px);
          margin-bottom: 1.5rem;
        }
        .p-card .p-card-body {
          padding: 1rem 1.75rem;
        }
        .p-card .p-card-content {
          padding: 0;
        }
      `,
    },

    chip: {
      css: () => `
        .p-disabled .p-chip-remove-icon {
          display: none;
        }
      `,
    },

    confirmdialog: {
      css: () => `
        .p-confirmdialog .p-dialog-content {
          align-items: baseline;
          gap: 0;
        }
        .p-confirmdialog .p-confirmdialog-reject-button {
          background-color: #dc3545;
          border-color: #dc3545;
          color: white;
        }
        .p-confirmdialog .p-confirmdialog-reject-button:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }
      `,
    },

    dataview: {
      content: {
        background: 'transparent',
      },
    },

    datatable: {
      root: {
        borderColor: 'transparent',
      },
      headerCell: {
        borderColor: 'transparent',
      },
      bodyCell: {
        padding: '0.45rem',
      },
      colorScheme: {
        light: {
          row: {
            stripedBackground: '{surface.100}',
          },
          headerCell: {
            color: '{surface.50}',
            background: '{surface.600}',
            padding: '0.45rem',
            hoverColor: '{surface.50}',
            hoverBackground: '{surface.600}',
            selectedColor: '{surface.50}',
            selectedBackground: '{surface.600}',
          },
        },
        dark: {
          headerCell: {
            hoverBackground: '{surface.600}',
            background: '{surface.600}',
            selectedBackground: '{surface.600}',
          },
        },
      },
      css: ({ dt }: any) => `
        .p-datatable-tbody > tr > td {
          font-size: 0.9rem;
          border: none !important;
          border-right: 1px solid ${dt('surface.200')} !important;
        }
        .p-datatable-tbody > tr > td:last-child {
          border-right: none !important;
        }
        .p-datatable-tbody > tr:hover,
        .p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover {
          background-color: ${dt('surface.200')} !important;
        }
        .p-datatable-row-selected {
          border-bottom: transparent !important;
          background-color: ${dt('surface.300')} !important;
        }
        .p-selectable-row:hover:not(.p-highlight) {
          background: #f5f5f5 !important;
        }
        .p-sortable-column-badge {
          margin: 0 0.5rem;
        }
        .p-datatable-column-filter-button {
          margin-left: 0.25rem;
        }
        .p-sortable-column-icon {
          margin-left: 0.5rem;
        }
        .active-filter .p-button {
          background-color: orange !important;
          color: var(--p-badge-primary-color);
        }
        .p-datatable-resizable-table > .p-datatable-tfoot > tr > td,
        .p-datatable-resizable-table > .p-datatable-tbody > tr > td {
          white-space: normal !important;
        }
        .p-datatable-loading-overlay .p-datatable-loading-icon {
          zoom: 5;
        }
        .p-selectable-row {
          cursor: pointer;
        }
        @media (prefers-color-scheme: dark) {
          .p-datatable-tbody > tr > td {
            border-right-color: ${dt('surface.700')} !important;
          }
          .p-datatable-tbody > tr:hover,
          .p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover {
            background-color: ${dt('surface.800')} !important;
          }
          .p-datatable-row-selected {
            background-color: ${dt('surface.800')} !important;
          }
          .p-selectable-row:hover:not(.p-highlight) {
            background: ${dt('surface.800')} !important;
          }
        }
      `,
    },

    datepicker: {
      css: () => `
        .p-datepicker,
        .p-datepicker .p-datepicker-header,
        .p-datepicker .p-datepicker-buttonbar,
        .p-datepicker table th,
        .p-datepicker table td {
          padding: 0;
        }
        .p-datepicker.ng-invalid .p-datepicker.p-inputwrapper .p-inputtext {
          box-shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px var(--p-form-field-invalid-border-color);
          border-color: var(--p-form-field-invalid-border-color) !important;
        }
        button.p-datepicker-dropdown:disabled {
          display: none;
        }
        .p-disabled .p-datepicker-dropdown {
          visibility: hidden;
        }
      `,
    },

    dialog: {
      css: () => `
        .p-dialog-content div.grid.fluid {
          margin: 5px 0 10px 0;
        }
      `,
    },

    fileupload: {
      progressbar: {
        height: '0',
      },
      css: () => `
        .p-fileupload .p-fileupload-buttonbar .p-button.p-fileupload-choose .p-button-label {
          margin-left: 0.5em;
        }
      `,
    },

    inputtext: {
      root: {
        disabledColor: 'var(--p-inputtext-color)',
      },
      css: () => `
        .p-inputtext[disabled] {
          box-shadow: none;
        }
        .p-inputtext:read-only:not(.p-autocomplete-multiple-container) {
          background: transparent;
          border: 1px solid transparent;
          box-shadow: none;
        }
        .p-inputtext:read-only:hover {
          border: 1px solid transparent;
        }
        .p-component::-webkit-input-placeholder {
          opacity: 0.5;
        }
        .p-component:read-only::-webkit-input-placeholder,
        .p-component:read-only::-moz-placeholder {
          color: transparent;
        }
      `,
    },

    menu: {
      css: () => `
        li.p-menu-item.p-hidden {
          display: none;
        }
      `,
    },

    multiselect: {
      root: {
        disabledBackground: 'transparent',
      },
      css: () => `
        .p-multiselect-label {
          display: flex;
          flex-wrap: wrap !important;
        }
        .p-multiselect.p-disabled {
          background: transparent;
          border: 1px solid transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
          box-shadow: none;
        }
        .p-multiselect.p-disabled > .p-multiselect-dropdown {
          display: none;
        }
        .p-multiselect.p-multiselect-chip .p-multiselect-token {
          padding: 0.25rem 0.5rem 0.35rem 0.5rem;
          border-radius: 4px;
          margin-bottom: 2px;
        }
      `,
    },

    select: {
      root: {
        disabledColor: '{form.field.color}',
      },
      css: () => `
        .p-select.p-disabled span,
        .p-disabled .p-chip-label {
          background: transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
        }
        .p-select.p-disabled {
          background: transparent;
          border: 1px solid transparent;
          box-shadow: none;
        }
        .p-select.p-disabled > .p-select-dropdown {
          display: none;
        }
        .p-disabled span.p-select-label {
          overflow: auto;
          text-wrap: pretty;
        }
      `,
    },

    slider: {
      colorScheme: {
        light: {
          handle: {
            content: {
              background: '{content.border.color}',
            },
          },
        },
        dark: {
          handle: {
            content: {
              background: '{content.border.color}',
            },
          },
        },
      },
    },

    splitbutton: {
      css: () => `
        .p-tieredmenu {
          font-size: 1rem;
        }
      `,
    },

    tabs: {
      colorScheme: {
        light: {
          tab: {
            activeBackground: '{primary.500}',
            activeColor: '{primary.50}',
          },
        },
        dark: {
          tab: {
            activeBackground: '{surface.600}',
            activeColor: '{surface.300}',
          },
        },
      },
      css: () => `
        .p-tab-active {
          border-radius: var(--p-border-radius-md) var(--p-border-radius-md) 0 0;
        }
      `,
    },

    tag: {
      css: () => `
        .p-tag {
          margin: 1px 0;
        }
      `,
    },

    textarea: {
      css: () => `
        .p-textarea[readonly],
        .p-textarea:disabled:not(.p-autocomplete-multiple-container) {
          border: 1px solid transparent;
          box-shadow: none;
          background: transparent;
        }
        .p-textarea[readonly]:focus {
          border: 1px solid transparent;
        }
        .p-textarea:read-only:hover {
          border: 1px solid transparent;
        }
      `,
    },

    togglebutton: {
      root: {
        background: '{surface.100}',
        borderColor: '{surface.100}',
        checkedBackground: '{primary.100}',
        checkedBorderColor: '{surface.100}',
      },
    },

    toolbar: {
      colorScheme: {
        light: {
          root: {
            color: '#000',
          },
        },
        dark: {
          root: {
            color: '#FFFFFF',
          },
        },
      },
      css: () => `
        .detail-toolbar {
          margin-bottom: 1rem;
          padding: 0.5rem;
          min-height: 50px;
          font-size: 1.5rem;
          font-weight: 500;
          border: none;
          color: var(--p-toolbar-color);
        }
        .list-toolbar.p-toolbar {
          padding: 0.5rem;
          font-size: 1.5rem;
          font-weight: 600;
          border: none;
          background: none;
        }
        .p-toolbar-group-left,
        .p-toolbar-group-center,
        .p-toolbar-group-right {
          align-items: center;
          display: flex;
        }
        .p-toolbar-group-center,
        .p-toolbar-group-right {
          flex-wrap: wrap;
        }
        @media screen and (max-width: 640px) {
          .p-toolbar p-button button:has(i.fa) .p-button-label,
          .p-toolbar p-button button:has(.p-button-icon-left) .p-button-label {
            display: none;
          }
        }
      `,
    },
  },
})

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.serviceWorkerEnabled,
        registrationStrategy: 'registerImmediately',
      }),
    ),
    provideRouter(APP_ROUTES, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: actedPresetTheme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'app-styles, primeng',
          },
        },
      },
    }),
    // must stay ahead of the other HTTP_INTERCEPTORS providers: MsalInterceptor runs first
    ...provideMsal(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ImsCommitHashInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorInterceptor,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService)
    }),
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'XXX',
    },
    MessageService,
    UserService,
    UtilitiesService,
    LongRunningJobService,
    TourService,
    ConfirmationService,
    NetworkService,
    TitleService,
    UpdateService,
    SignalService,
    ConfigureFiltersService,
  ],
}
